var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { isBefore, isPast } from "date-fns";
import { IdentifiedPatientState, primaryConsentTypes } from "healthex-common/consent.dtos";
import { ContactMethod } from "healthex-common/consentconfig.dtos";
import { PatientDataVisualizationPdfStatus } from "healthex-common/patient.data.visualization.dtos";
import { UserType } from "healthex-common/user.dtos";
import { action, computed, observable, reaction } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { FILTER_ALL, FILTER_GROUP_PROCEDURE_DIAGNOSTIC, FILTER_GROUP_VISITS } from "./PatientHistory.types";
import { NamedRoutes } from "./NamedRoutes";
import { NavigationHelper } from "./NavigationHelper";
import { findMatchingNamedRouteParent } from "./RoutingUIManager";
import { openAsyncWindow } from "./WindowHelpers";
import { SharingSection, parseSharingSection } from "./PatientStudiesListViewModel";
import { slugifyOptional } from "healthex-common/utils";
const _patientResearchDashboardRoutes = {
    RESEARCH: NamedRoutes.PATIENT_SHARING,
    MY_HEALTHEX: NamedRoutes.PATIENT_HOME,
    HISTORY: NamedRoutes.PATIENT_HISTORY,
};
const patientResearchDashboardRoutes = _patientResearchDashboardRoutes;
export var MobileBottomNavigationButtonType;
(function (MobileBottomNavigationButtonType) {
    MobileBottomNavigationButtonType[MobileBottomNavigationButtonType["HISTORY"] = 0] = "HISTORY";
    MobileBottomNavigationButtonType[MobileBottomNavigationButtonType["RESEARCH"] = 1] = "RESEARCH";
})(MobileBottomNavigationButtonType || (MobileBottomNavigationButtonType = {}));
export class PatientResearchDashboardUIManager {
    constructor(routingUIManager, studyManager, shlUIManager, shlService, documentRetrievalManager, authManager, patientPreferencesManager, aiPartnerService) {
        this.routingUIManager = routingUIManager;
        this.studyManager = studyManager;
        this.shlUIManager = shlUIManager;
        this.shlService = shlService;
        this.documentRetrievalManager = documentRetrievalManager;
        this.authManager = authManager;
        this.patientPreferencesManager = patientPreferencesManager;
        this.aiPartnerService = aiPartnerService;
        this.patientResearchDashboardNavigationHelper = new NavigationHelper(this.routingUIManager, patientResearchDashboardRoutes);
        this.mostRecentRetrieval = NOT_LOADED;
        this.selectedFiltersByGroup = new Map([
            [FILTER_GROUP_VISITS, [FILTER_ALL]],
            [FILTER_GROUP_PROCEDURE_DIAGNOSTIC, [FILTER_ALL]],
        ]);
        this.selectedTopRowFilters = [FILTER_ALL];
        this.availableAIPartners = NOT_LOADED;
        this.pollRetrievalInterval = null;
        reaction(() => this.isOnPatientHistoryArea, (isOnHistory) => {
            if (!isOnHistory)
                this.resetFilters();
        });
        reaction(() => this.isOnConnectionsSection, async (isOnConnections) => {
            if (isOnConnections)
                await this.refreshStudies();
        }, { fireImmediately: true });
        reaction(() => this.isOnSharedLinksSection, async (isOnSharedLinks) => {
            if (isOnSharedLinks)
                await this.refreshPatientSHLs();
        }, { fireImmediately: true });
        reaction(() => [this.authManager.isLoggedIn, this.shouldLoadMostRecentRetrieval], async () => {
            await this.loadMostRecentRetrieval();
        }, { fireImmediately: true });
        reaction(() => this.isUnifiedRecordProcessing, async () => {
            if (this.pollRetrievalInterval !== null) {
                window.clearInterval(this.pollRetrievalInterval);
                this.pollRetrievalInterval = null;
            }
            if (this.isUnifiedRecordProcessing === true) {
                this.pollRetrievalInterval = window.setInterval(async () => {
                    await this.loadMostRecentRetrieval();
                }, 10000);
            }
        }, { fireImmediately: true });
    }
    async loadMostRecentRetrieval() {
        if (!this.shouldLoadMostRecentRetrieval)
            return;
        if (!this.authManager.isLoggedIn || this.authManager.userType !== UserType.PATIENT) {
            this.mostRecentRetrieval = null;
            return;
        }
        this.mostRecentRetrieval = await this.documentRetrievalManager.getMostRecentDownloadableOrProcessingTaskBatch();
    }
    get hasUnifiedRecordExpired() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED)
            return NOT_LOADED;
        return ((_a = this.mostRecentRetrieval) === null || _a === void 0 ? void 0 : _a.patientDeliveryExpiration) && isPast(this.mostRecentRetrieval.patientDeliveryExpiration);
    }
    get unifiedRecordSummary() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED)
            return NOT_LOADED;
        if (!this.mostRecentRetrieval)
            return null;
        return (_a = this.mostRecentRetrieval.patientData) !== null && _a !== void 0 ? _a : null;
    }
    get retrievalContactMethod() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED)
            return NOT_LOADED;
        if (!this.mostRecentRetrieval)
            return null;
        return (_a = this.mostRecentRetrieval.patientContactMethod) !== null && _a !== void 0 ? _a : null;
    }
    get preferredContactMethod() {
        var _a, _b;
        if (this.retrievalContactMethod !== NOT_LOADED && this.retrievalContactMethod)
            return this.retrievalContactMethod;
        if (this.patientPreferencesManager.preferences.value === NOT_LOADED)
            return NOT_LOADED;
        return (_b = (_a = this.patientPreferencesManager.preferences.value) === null || _a === void 0 ? void 0 : _a.preferredContactMethod) !== null && _b !== void 0 ? _b : ContactMethod.EMAIL;
    }
    get contactMethodWhenUnifiedRecordSent() {
        var _a, _b;
        if (this.mostRecentRetrieval === NOT_LOADED)
            return NOT_LOADED;
        return (_b = (_a = this.mostRecentRetrieval) === null || _a === void 0 ? void 0 : _a.patientContactMethod) !== null && _b !== void 0 ? _b : null;
    }
    get isUnifiedRecordDownloadable() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED || this.unifiedRecordSummary === NOT_LOADED)
            return NOT_LOADED;
        return ((_a = this.mostRecentRetrieval) === null || _a === void 0 ? void 0 : _a.pdfStatus) === PatientDataVisualizationPdfStatus.COMPLETED && !!this.unifiedRecordSummary;
    }
    get isUnifiedRecordProcessing() {
        if (this.isUnifiedRecordDownloadable === NOT_LOADED)
            return NOT_LOADED;
        if (!this.mostRecentRetrieval)
            return false;
        return !this.isUnifiedRecordDownloadable;
    }
    get shouldLoadMostRecentRetrieval() {
        return !!findMatchingNamedRouteParent(this.routingUIManager.currentRoute, [NamedRoutes.PATIENT_HOME, NamedRoutes.PATIENT_HISTORY]);
    }
    get isOnPatientHistoryArea() {
        return !!findMatchingNamedRouteParent(this.routingUIManager.currentRoute, [NamedRoutes.PATIENT_HISTORY]);
    }
    resetFilters() {
        this.selectedFiltersByGroup = new Map([
            [FILTER_GROUP_VISITS, [FILTER_ALL]],
            [FILTER_GROUP_PROCEDURE_DIAGNOSTIC, [FILTER_ALL]],
        ]);
        this.selectedTopRowFilters = [FILTER_ALL];
    }
    get isOnPatientSharingPage() {
        if (this.authManager.userType !== "PATIENT")
            return false;
        return !!findMatchingNamedRouteParent(this.routingUIManager.currentRoute, [NamedRoutes.PATIENT_SHARING]);
    }
    get currentSharingSection() {
        var _a;
        if (!this.isOnPatientSharingPage)
            return null;
        return parseSharingSection((_a = this.routingUIManager.currentRouteParams) === null || _a === void 0 ? void 0 : _a.section);
    }
    get isOnConnectionsSection() {
        return this.currentSharingSection === SharingSection.CONNECTIONS;
    }
    get isOnSharedLinksSection() {
        return this.currentSharingSection === SharingSection.SHARED_LINKS;
    }
    get selectedBottomNavigationButton() {
        switch (this.patientResearchDashboardNavigationHelper.currentNavigationItem) {
            case "RESEARCH":
                return MobileBottomNavigationButtonType.RESEARCH;
            case "MY_HEALTHEX":
                return null;
            case "HISTORY":
                return MobileBottomNavigationButtonType.HISTORY;
            default:
        }
        return null;
    }
    get noConsentsAvailable() {
        if (this.studyManager.allPatientConsentsByStudyForSearchTotals.value === NOT_LOADED)
            return NOT_LOADED;
        return this.studyManager.allPatientConsentsByStudyForSearchTotals.value.all === 0;
    }
    get filteredAvailablePartners() {
        const currentStudies = this.studyManager.allPatientConsentsByStudyForSearch.value;
        if (this.availableAIPartners === NOT_LOADED || currentStudies === NOT_LOADED)
            return [];
        const connectedStudyIds = new Set(currentStudies.map((s) => s.id));
        return this.availableAIPartners.filter((p) => !connectedStudyIds.has(p.projectId));
    }
    get sortedStudies() {
        if (this.currentStudies === NOT_LOADED)
            return NOT_LOADED;
        const withMultipleEntriesPerStudy = [];
        this.currentStudies.forEach((studyWithConsents) => {
            const { consents, ...study } = studyWithConsents;
            const pendingConsents = consents.filter((consent) => consent.pending);
            const consentedConsents = consents.filter((consent) => !consent.pending);
            if (pendingConsents.length > 0) {
                withMultipleEntriesPerStudy.push({ study, consents: pendingConsents });
            }
            if (consentedConsents.length > 0) {
                withMultipleEntriesPerStudy.push({ study, consents: consentedConsents });
            }
        });
        return (withMultipleEntriesPerStudy
            .sort((a, b) => {
            var _a, _b;
            const aTimestamp = (_a = a.consents.find((consent) => primaryConsentTypes.includes(consent.consentType))) === null || _a === void 0 ? void 0 : _a.timestamp;
            const bTimestamp = (_b = b.consents.find((consent) => primaryConsentTypes.includes(consent.consentType))) === null || _b === void 0 ? void 0 : _b.timestamp;
            if (aTimestamp && bTimestamp) {
                return isBefore(new Date(aTimestamp), new Date(bTimestamp)) ? 1 : -1;
            }
            return 0;
        })
            .sort((a, b) => {
            var _a, _b;
            const isAEnrolling = ((_a = a.consents.find((consent) => primaryConsentTypes.includes(consent.consentType))) === null || _a === void 0 ? void 0 : _a.state) ===
                IdentifiedPatientState.ENROLLING;
            const isBEnrolling = ((_b = b.consents.find((consent) => primaryConsentTypes.includes(consent.consentType))) === null || _b === void 0 ? void 0 : _b.state) ===
                IdentifiedPatientState.ENROLLING;
            if (isAEnrolling && isBEnrolling)
                return 0;
            if (isAEnrolling && !isBEnrolling)
                return -1;
            if (!isAEnrolling && isBEnrolling)
                return 1;
            return 0;
        }));
    }
    get currentStudies() {
        if (!this.isOnPatientSharingPage)
            return NOT_LOADED;
        return this.studyManager.allPatientConsentsByStudyForSearch.value;
    }
    async refreshStudies() {
        if (!this.isOnConnectionsSection)
            return;
        await Promise.all([this.studyManager.allPatientConsentsByStudyForSearch.waitForValue(), this.fetchAvailableAIPartners()]);
    }
    async fetchAvailableAIPartners() {
        try {
            const partners = await this.aiPartnerService.getAvailablePartners();
            this.availableAIPartners = partners;
        }
        catch (e) {
            console.warn("[PatientResearchDashboardUIManager] Failed to fetch available AI partners", e);
            this.availableAIPartners = [];
        }
    }
    async refreshPatientSHLs() {
        if (!this.isOnSharedLinksSection)
            return;
        await this.shlUIManager.patientSHLsForSearch.waitForValue();
    }
    goToPage(value, sharingSection) {
        switch (value) {
            case MobileBottomNavigationButtonType.RESEARCH:
                this.patientResearchDashboardNavigationHelper.navigateTo("RESEARCH", {
                    section: slugifyOptional(sharingSection),
                });
                break;
            case MobileBottomNavigationButtonType.HISTORY:
                this.patientResearchDashboardNavigationHelper.navigateTo("HISTORY");
                break;
        }
    }
    async getDownloadPdfLink() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED || this.mostRecentRetrieval === null)
            return null;
        return await this.documentRetrievalManager.getDownloadPdfLink((_a = this.mostRecentRetrieval) === null || _a === void 0 ? void 0 : _a.batchId);
    }
    async getDownloadRawDocumentsLink() {
        var _a;
        if (this.mostRecentRetrieval === NOT_LOADED || this.mostRecentRetrieval === null)
            return null;
        return await this.documentRetrievalManager.getDownloadDocumentsLink((_a = this.mostRecentRetrieval) === null || _a === void 0 ? void 0 : _a.batchId);
    }
    async openUnifiedRecordDownloadInNewWindow(mode, options) {
        return openAsyncWindow(async () => {
            var _a, _b, _c;
            (_a = options === null || options === void 0 ? void 0 : options.onBeforeFetch) === null || _a === void 0 ? void 0 : _a.call(options);
            const dto = mode === "raw" ? await this.getDownloadRawDocumentsLink() : await this.getDownloadPdfLink();
            const url = (_b = dto === null || dto === void 0 ? void 0 : dto.downloadLink) !== null && _b !== void 0 ? _b : null;
            if (!url) {
                console.warn(`[PatientResearchDashboardUIManager] No unified record download link for mode "${mode}" (link unavailable or batch missing).`);
                (_c = options === null || options === void 0 ? void 0 : options.onNoDownloadLink) === null || _c === void 0 ? void 0 : _c.call(options);
                return null;
            }
            return url;
        });
    }
}
__decorate([
    observable
], PatientResearchDashboardUIManager.prototype, "mostRecentRetrieval", void 0);
__decorate([
    observable
], PatientResearchDashboardUIManager.prototype, "selectedFiltersByGroup", void 0);
__decorate([
    observable
], PatientResearchDashboardUIManager.prototype, "selectedTopRowFilters", void 0);
__decorate([
    observable
], PatientResearchDashboardUIManager.prototype, "availableAIPartners", void 0);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "hasUnifiedRecordExpired", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "unifiedRecordSummary", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "retrievalContactMethod", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "preferredContactMethod", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "contactMethodWhenUnifiedRecordSent", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "isUnifiedRecordDownloadable", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "shouldLoadMostRecentRetrieval", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "isOnPatientHistoryArea", null);
__decorate([
    action
], PatientResearchDashboardUIManager.prototype, "resetFilters", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "isOnPatientSharingPage", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "currentSharingSection", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "isOnConnectionsSection", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "isOnSharedLinksSection", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "selectedBottomNavigationButton", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "noConsentsAvailable", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "filteredAvailablePartners", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "sortedStudies", null);
__decorate([
    computed
], PatientResearchDashboardUIManager.prototype, "currentStudies", null);
__decorate([
    action
], PatientResearchDashboardUIManager.prototype, "fetchAvailableAIPartners", null);
